import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import { track } from "../utils/track";
import { motion } from "framer-motion";
import {
  Key,
  Copy,
  RefreshCw,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  Code,
  Book,
  Terminal,
  FileText,
  Zap,
  Clock,
  Bot,
  ExternalLink,
} from "lucide-react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

const AGENT_PROMPT = `Use the WhappScan API to extract data from documents.
Spec: https://whappscan.com/openapi.json  (docs: https://whappscan.com/en/api)
Auth: header X-API-Key from the env var WHAPPSCAN_KEY.
Flow: POST /api/v1/process (multipart, field "file") -> result_id -> poll GET /api/v1/results/{id} every 2-3 s until status is "done".
Read extracted_data.fields[].value. On 402 tell me the next_action; on 429 wait Retry-After; files must be under ~4.4 MB.`;

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export default function ApiPage() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [promptCopied, setPromptCopied] = useState(false);
  const [apiKeyData, setApiKeyData] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmRegenerate, setShowConfirmRegenerate] = useState(false);

  const fetchApiKey = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getApiKey();
      setApiKeyData(response);
    } catch (err) {
      console.error("Error fetching API key:", err);
      setError("fetch_error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApiKey();
  }, [fetchApiKey]);

  const handleCopy = async () => {
    if (apiKeyData?.api_key) {
      try {
        await navigator.clipboard.writeText(apiKeyData.api_key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    }
  };

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      setError(null);
      // Primera clave de la cuenta = paso del embudo de la API (api_key_create); si ya había, es una rotación.
      track(apiKeyData?.has_api_key ? "api_key_regenerate" : "api_key_create");
      const response = await api.regenerateApiKey();
      setApiKeyData({
        ...apiKeyData,
        api_key: response.api_key,
        api_key_prefix: response.api_key_prefix || String(response.api_key || "").slice(0, 8),
        created_at: response.created_at,
        has_api_key: true,
      });
      setShowKey(true);
      setShowConfirmRegenerate(false);
    } catch (err) {
      console.error("Error regenerating API key:", err);
      setError("regenerate_error");
    } finally {
      setRegenerating(false);
    }
  };

  const maskKey = (key) => {
    if (!key) return "";
    return key.slice(0, 8) + "•".repeat(24) + key.slice(-4);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Dominio público de la API (los ejemplos que ve el usuario). La app sigue hablando con el endpoint de API Gateway.
  const baseUrl = "https://api.whappscan.com";

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg">
              <Code className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("api.title")}
              </h1>
              <p className="text-gray-500">{t("api.subtitle")}</p>
            </div>
          </div>
        </motion.div>

        {/* API Key Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-50 to-green-100 text-primary-600">
              <Key className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t("api.your_key")}
            </h2>
          </div>

          {apiKeyData?.has_api_key ? (
            <div className="space-y-4">
              {/* Key Display: la clave completa solo existe en esta pantalla justo después de crearla/regenerarla;
                  después el servidor solo guarda su huella (hash) y aquí se ve el prefijo. */}
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gray-900 rounded-xl px-4 py-3 font-mono text-sm text-gray-200 flex items-center justify-between">
                  <span className="select-all break-all" data-testid="api-key-display">
                    {apiKeyData.api_key
                      ? showKey
                        ? apiKeyData.api_key
                        : maskKey(apiKeyData.api_key)
                      : `${apiKeyData.api_key_prefix || "wsk_"}…`}
                  </span>
                  {apiKeyData.api_key && (
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => setShowKey(!showKey)}
                        className="p-1.5 rounded-lg hover:bg-gray-700 transition-colors text-gray-400 hover:text-white"
                        title={showKey ? t("api.hide_key") : t("api.show_key")}
                      >
                        {showKey ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={handleCopy}
                        className={cn(
                          "p-1.5 rounded-lg transition-colors",
                          copied
                            ? "bg-green-500/20 text-green-400"
                            : "hover:bg-gray-700 text-gray-400 hover:text-white"
                        )}
                        title={t("api.copy_key")}
                      >
                        {copied ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-500" data-testid="api-key-note">
                {apiKeyData.api_key
                  ? t("api.key_shown_once", "Copy it now: this is the only time the full key is shown. If you lose it, regenerate a new one.")
                  : t("api.key_hidden", "For your security the full key is no longer shown. To see a new key, regenerate it (the current one will stop working).")}
              </p>

              {/* Key Metadata */}
              <div className="flex flex-wrap gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>
                    {t("api.created_at")}: {formatDate(apiKeyData.created_at)}
                  </span>
                </div>
                {apiKeyData.last_used_at && (
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    <span>
                      {t("api.last_used")}: {formatDate(apiKeyData.last_used_at)}
                    </span>
                  </div>
                )}
              </div>

              {/* Regenerate Button */}
              <div className="pt-4 border-t border-gray-100">
                {showConfirmRegenerate ? (
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-amber-600">
                      <AlertTriangle className="w-4 h-4 inline mr-1" />
                      {t("api.regenerate_warning")}
                    </p>
                    <button
                      onClick={handleRegenerate}
                      disabled={regenerating}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {regenerating && (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      )}
                      {t("api.confirm_regenerate")}
                    </button>
                    <button
                      onClick={() => setShowConfirmRegenerate(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      {t("common.cancel")}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowConfirmRegenerate(true)}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    {t("api.regenerate")}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Key className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">{t("api.no_key")}</p>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {regenerating && (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                )}
                {t("api.generate_key")}
              </button>
            </div>
          )}
        </motion.div>

        {/* Límites del plan, documentación pública y receta para agentes (2-10-2026: la API está abierta a todos los planes) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-gradient-to-br from-violet-50 to-purple-100 text-violet-600">
              <Bot className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t("api.limits_title", "Your plan limits and AI agents")}
            </h2>
          </div>
          <ul className="text-sm text-gray-600 space-y-1 mb-4 list-disc ml-5">
            <li>
              {t("api.limits_pages", "Each page uses one page of your monthly quota, shared with the web app, WhatsApp, email and Telegram (free plan: 20 pages a month).")}
            </li>
            <li>
              {t("api.limits_rate", "Requests per minute per key: {{n}}.", { n: apiKeyData?.limits?.rate_limit_per_minute || 60 })}
            </li>
            <li>
              {t("api.limits_file", "Files up to about {{mb}} MB and 50 pages per document.", { mb: apiKeyData?.limits?.max_file_mb || 4.3 })}
            </li>
          </ul>
          <div className="flex flex-wrap gap-3 mb-4">
            <a
              href={`https://whappscan.com/${i18n.language === "es" ? "es" : "en"}/api`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
            >
              <ExternalLink className="w-4 h-4" />
              {t("api.public_docs", "Public documentation")}
            </a>
            <a
              href="https://whappscan.com/openapi.json"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50"
            >
              <ExternalLink className="w-4 h-4" />
              {t("api.openapi_spec", "OpenAPI spec")}
            </a>
          </div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            {t("api.agent_prompt_title", "Paste this into your AI agent (Claude Code, Cursor…)")}
          </p>
          <div className="relative rounded-xl bg-gray-900">
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(AGENT_PROMPT);
                  setPromptCopied(true);
                  track("api_agent_prompt_copy");
                  setTimeout(() => setPromptCopied(false), 2000);
                } catch (err) {
                  console.error("Failed to copy:", err);
                }
              }}
              className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-xs text-gray-200 hover:bg-white/20"
            >
              {promptCopied ? <CheckCircle className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {promptCopied ? "OK" : t("api.copy_key_short", "Copy")}
            </button>
            <pre className="m-0 overflow-x-auto p-4 pt-9 text-xs leading-5 text-green-100 whitespace-pre-wrap">{AGENT_PROMPT}</pre>
          </div>
        </motion.div>

        {/* Documentation Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-100 text-blue-600">
              <Book className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t("api.documentation")}
            </h2>
          </div>

          <div className="space-y-6">
            {/* Process Auto Endpoint */}
            <div className="border border-violet-200 bg-violet-50/30 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-violet-100 text-violet-700 text-xs font-semibold rounded">
                  POST
                </span>
                <code className="text-sm text-gray-700 font-bold break-all">
                  /api/v1/process
                </code>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full uppercase tracking-wider animate-pulse">
                  {t("common.new")}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3 font-medium">
                {t("api.process_auto_description") || "Auto-detect scanner and process document. Recommended for generic workflows."}
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`curl -X POST "${baseUrl}/api/v1/process" \\
  -H "X-API-Key: wsk_your_api_key" \\
  -F "file=@document.pdf"`}</code>
                </pre>
              </div>
            </div>

            {/* Upload Endpoint */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                  POST
                </span>
                <code className="text-sm text-gray-700 break-all">
                  /api/v1/scanners/{"{scannerId}"}/upload
                </code>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {t("api.upload_description")}
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`curl -X POST "${baseUrl}/api/v1/scanners/{scannerId}/upload" \\
  -H "X-API-Key: wsk_your_api_key" \\
  -F "file=@document.pdf"`}</code>
                </pre>
              </div>
            </div>

            {/* Get Result Endpoint */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                  GET
                </span>
                <code className="text-sm text-gray-700 break-all">
                  /api/v1/results/{"{resultId}"}
                </code>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {t("api.result_description")}
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`curl "${baseUrl}/api/v1/results/{resultId}" \\
  -H "X-API-Key: wsk_your_api_key"`}</code>
                </pre>
              </div>
            </div>

            {/* List Scanners Endpoint */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                  GET
                </span>
                <code className="text-sm text-gray-700 break-all">/api/v1/scanners</code>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {t("api.scanners_description")}
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`curl "${baseUrl}/api/v1/scanners" \\
  -H "X-API-Key: wsk_your_api_key"`}</code>
                </pre>
              </div>
            </div>

            {/* Create Scanner Endpoint */}
            <div className="border border-violet-200 bg-violet-50/30 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-violet-100 text-violet-700 text-xs font-semibold rounded">
                  POST
                </span>
                <code className="text-sm text-gray-700 font-bold break-all">
                  /api/v1/scanners
                </code>
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full uppercase tracking-wider animate-pulse">
                  {t("common.new")}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-3 font-medium">
                {t("api.create_description")}
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`curl -X POST "${baseUrl}/api/v1/scanners" \\
  -H "X-API-Key: wsk_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Facturas de Proveedor",
    "description": "Proveedor, factura y líneas",
    "fields": [
      { "name": "proveedor_nif", "type": "string", "required": true },
      { "name": "factura_fecha", "type": "date" },
      { "name": "factura_importe", "type": "number" },
      {
        "name": "lineas",
        "type": "table",
        "item_fields": [
          { "name": "codigo", "type": "string" },
          { "name": "cantidad", "type": "number" },
          { "name": "precio", "type": "number" },
          { "name": "unidad", "type": "string" }
        ]
      }
    ]
  }'`}</code>
                </pre>
              </div>
            </div>

            {/* Get Scanner Endpoint */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                  GET
                </span>
                <code className="text-sm text-gray-700 break-all">
                  /api/v1/scanners/{"{scannerId}"}
                </code>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {t("api.get_one_description")}
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`curl "${baseUrl}/api/v1/scanners/{scannerId}" \\
  -H "X-API-Key: wsk_your_api_key"`}</code>
                </pre>
              </div>
            </div>

            {/* Update Scanner Endpoint */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs font-semibold rounded">
                  PATCH
                </span>
                <code className="text-sm text-gray-700 break-all">
                  /api/v1/scanners/{"{scannerId}"}
                </code>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {t("api.update_description")}
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`curl -X PATCH "${baseUrl}/api/v1/scanners/{scannerId}" \\
  -H "X-API-Key: wsk_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{ "name": "Nuevo nombre" }'`}</code>
                </pre>
              </div>
            </div>

            {/* Delete Scanner Endpoint */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">
                  DELETE
                </span>
                <code className="text-sm text-gray-700 break-all">
                  /api/v1/scanners/{"{scannerId}"}
                </code>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {t("api.delete_description")}
              </p>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`curl -X DELETE "${baseUrl}/api/v1/scanners/{scannerId}" \\
  -H "X-API-Key: wsk_your_api_key"`}</code>
                </pre>
              </div>
            </div>

            {/* Response Example */}
            <div className="border border-gray-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  {t("api.response_example")}
                </span>
              </div>
              <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                <pre className="text-sm text-gray-300 overflow-x-auto">
                  <code>{`{
  "success": true,
  "result_id": "abc123-uuid",
  "status": "done",
  "extracted_data": {
    "field_1": "value_1",
    "field_2": "value_2"
  },
  "confidence": 0.95
}`}</code>
                </pre>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Tips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid md:grid-cols-4 gap-4"
        >
          <div className="bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl p-5">
            <div className="p-2 rounded-lg bg-violet-100 text-violet-600 w-fit mb-3">
              <Terminal className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t("api.tip_auth_title")}
            </h3>
            <p className="text-sm text-gray-500">{t("api.tip_auth_description")}</p>
          </div>

          <div className="bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl p-5">
            <div className="p-2 rounded-lg bg-emerald-100 text-emerald-600 w-fit mb-3">
              <Zap className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t("api.tip_speed_title")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("api.tip_speed_description")}
            </p>
          </div>

          <div className="bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl p-5">
            <div className="p-2 rounded-lg bg-amber-100 text-amber-600 w-fit mb-3">
              <Zap className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t("api.tip_tokens_title")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("api.tip_tokens_description")}
            </p>
          </div>

          <div className="bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl p-5">
            <div className="p-2 rounded-lg bg-green-100 text-green-600 w-fit mb-3">
              <FileText className="w-5 h-5" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {t("api.tip_formats_title")}
            </h3>
            <p className="text-sm text-gray-500">
              {t("api.tip_formats_description")}
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
